import React, { useEffect, useState } from "react";
import { api, imageUrl } from "@/lib/api";
import { toast } from "sonner";
import Lightbox from "@/components/Lightbox";
import { Bug, Copy, Trash2, RefreshCw, ChevronDown, ChevronUp } from "lucide-react";

const SEVERITY_COLORS = {
  low: "#5FBE7E",
  medium: "#FFB300",
  high: "#E5263F",
};
const STATUS_COLORS = {
  open: "#E5263F",
  in_progress: "#FFB300",
  closed: "#5FBE7E",
};

const STATUS_LABEL = {
  open: "Open",
  in_progress: "In progress",
  closed: "Closed",
};

const BugReports = () => {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ open: 0, in_progress: 0, closed: 0, all: 0 });
  const [filter, setFilter] = useState("open");
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [lightboxFor, setLightboxFor] = useState(null);

  const loadCounts = async () => {
    try {
      const { data } = await api.get("/bug-reports/counts");
      setCounts({
        open: data.open || 0,
        in_progress: data.in_progress || 0,
        closed: data.closed || 0,
        all: data.all ?? data.total ?? 0,
      });
    } catch (e) {
      console.warn("Could not load bug counts:", e);
    }
  };

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/bug-reports", {
        params: filter === "all" ? {} : { status: filter },
      });
      setRows(data || []);
    } catch {
      toast.error("Could not load bug reports");
    } finally {
      setBusy(false);
    }
    loadCounts();
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const updateStatus = async (row, status) => {
    try {
      await api.put(`/bug-reports/${row.id}`, { status });
      toast.success(`Marked ${STATUS_LABEL[status]}`);
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const remove = async (row) => {
    if (!window.confirm("Delete this bug report?")) return;
    try {
      await api.delete(`/bug-reports/${row.id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const copyForDev = async (row) => {
    const snippet =
      "BUG REPORT (Footwork stock)\n" +
      "============================\n" +
      `ID         : ${row.id}\n` +
      `Severity   : ${row.severity}\n` +
      `Category   : ${row.category}\n` +
      `Status     : ${row.status}\n` +
      `Page       : ${row.page_path}\n` +
      `URL        : ${row.page_url}\n` +
      `When       : ${row.created_at}\n` +
      `User       : ${row.submitted_by_name} (${row.submitted_by_role})${
        row.submitted_by_department ? " · " + row.submitted_by_department : ""
      }\n` +
      `Viewport   : ${row.viewport}\n` +
      `User agent : ${row.user_agent}\n\n` +
      "DESCRIPTION\n-----------\n" +
      (row.description || "");
    try {
      await navigator.clipboard.writeText(snippet);
      toast.success("Copied — paste into your developer's chat");
    } catch {
      toast.error("Clipboard blocked");
    }
  };

  const sevColor = (s) => SEVERITY_COLORS[s] || "#607087";
  const statColor = (s) => STATUS_COLORS[s] || "#607087";

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="bug-reports-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-5">
        <div>
          <div className="label-eyebrow">Submitted by staff · last 500</div>
          <h1 className="font-display text-5xl leading-none mt-2 flex items-center gap-3">
            <Bug size={36} strokeWidth={2.5} className="text-[#E5263F]" />
            Bug reports
          </h1>
        </div>
        <button
          data-testid="bug-reports-refresh"
          onClick={load}
          className="nb-btn bg-white px-4 py-2.5 flex items-center gap-2"
          disabled={busy}
        >
          <RefreshCw size={16} strokeWidth={2.5} /> Refresh
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-5">
        {["open", "in_progress", "closed", "all"].map((s) => (
          <button
            key={s}
            data-testid={`bug-filter-${s}`}
            onClick={() => setFilter(s)}
            className={`nb-btn px-3 py-2 text-sm flex items-center gap-2 ${
              filter === s ? "nb-btn-dark" : "bg-white"
            }`}
          >
            {s === "all" ? "All" : STATUS_LABEL[s]}
            <span
              className="text-[10px] font-bold px-1.5 py-0.5 rounded-full"
              style={{
                background: filter === s ? "rgba(255,255,255,0.15)" : "#F0F0F0",
              }}
            >
              {counts[s] ?? 0}
            </span>
          </button>
        ))}
      </div>

      {rows.length === 0 ? (
        <div className="nb-card p-10 text-center text-[#607087]" data-testid="bug-empty">
          <Bug size={48} strokeWidth={2} className="mx-auto opacity-30 mb-3" />
          No bug reports {filter === "all" ? "yet" : `marked "${STATUS_LABEL[filter] || filter}"`}.
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {rows.map((row) => {
            const isOpen = !!expanded[row.id];
            return (
              <div
                key={row.id}
                className="nb-card p-4"
                data-testid={`bug-row-${row.id}`}
              >
                <div className="flex items-start gap-3 flex-wrap">
                  <span
                    className="text-[10px] font-bold tracking-wide px-2 py-0.5 rounded-full uppercase"
                    style={{
                      background: `${sevColor(row.severity)}1A`,
                      color: sevColor(row.severity),
                      border: `1px solid ${sevColor(row.severity)}55`,
                    }}
                  >
                    {row.severity}
                  </span>
                  <span
                    className="text-[10px] font-bold tracking-wide px-2 py-0.5 rounded-full"
                    style={{
                      background: `${statColor(row.status)}1A`,
                      color: statColor(row.status),
                      border: `1px solid ${statColor(row.status)}55`,
                    }}
                  >
                    {STATUS_LABEL[row.status] || row.status}
                  </span>
                  <span className="label-eyebrow">{row.category}</span>
                  <span className="label-eyebrow text-[#607087]">
                    {row.page_path}
                  </span>
                  <span className="label-eyebrow text-[#607087] ml-auto">
                    {new Date(row.created_at).toLocaleString()}
                  </span>
                </div>
                <div
                  className={`mt-2 text-sm whitespace-pre-wrap ${
                    isOpen ? "" : "line-clamp-3"
                  }`}
                >
                  {row.description}
                </div>
                {row.screenshot_path && (
                  <button
                    type="button"
                    data-testid={`bug-screenshot-${row.id}`}
                    onClick={() => setLightboxFor([row.screenshot_path])}
                    className="mt-3 block"
                    aria-label="open screenshot"
                  >
                    <img
                      src={imageUrl(row.screenshot_path)}
                      alt="screenshot"
                      className="max-h-44 rounded-xl border border-[#001740]/15 hover:ring-2 hover:ring-[#228EBF]/40 transition"
                    />
                  </button>
                )}
                <div className="flex items-center gap-2 mt-3 flex-wrap text-xs text-[#607087]">
                  <span>
                    by <b className="text-[#001740]">{row.submitted_by_name}</b>
                  </span>
                  {row.submitted_by_role && <span>· {row.submitted_by_role}</span>}
                  {row.submitted_by_department && (
                    <span>· {row.submitted_by_department}</span>
                  )}
                </div>
                {isOpen && (
                  <div className="mt-2 nb-card-sm p-2 text-[11px] font-mono-num text-[#607087]">
                    <div>
                      <b>URL:</b> {row.page_url}
                    </div>
                    <div>
                      <b>Viewport:</b> {row.viewport}
                    </div>
                    <div className="truncate">
                      <b>UA:</b> {row.user_agent}
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-2 mt-3 flex-wrap">
                  <button
                    data-testid={`bug-expand-${row.id}`}
                    onClick={() =>
                      setExpanded((e) => ({ ...e, [row.id]: !e[row.id] }))
                    }
                    className="nb-btn bg-white px-3 py-1.5 text-xs flex items-center gap-1"
                  >
                    {isOpen ? (
                      <>
                        <ChevronUp size={12} /> Less
                      </>
                    ) : (
                      <>
                        <ChevronDown size={12} /> More
                      </>
                    )}
                  </button>
                  <button
                    data-testid={`bug-copy-${row.id}`}
                    onClick={() => copyForDev(row)}
                    className="nb-btn bg-white px-3 py-1.5 text-xs flex items-center gap-1"
                  >
                    <Copy size={12} strokeWidth={2.5} /> Copy for developer
                  </button>
                  {row.status !== "in_progress" && (
                    <button
                      data-testid={`bug-mark-progress-${row.id}`}
                      onClick={() => updateStatus(row, "in_progress")}
                      className="nb-btn bg-white px-3 py-1.5 text-xs"
                    >
                      Mark in progress
                    </button>
                  )}
                  {row.status !== "closed" && (
                    <button
                      data-testid={`bug-mark-closed-${row.id}`}
                      onClick={() => updateStatus(row, "closed")}
                      className="nb-btn nb-btn-success px-3 py-1.5 text-xs"
                    >
                      Close
                    </button>
                  )}
                  {row.status !== "open" && (
                    <button
                      data-testid={`bug-mark-open-${row.id}`}
                      onClick={() => updateStatus(row, "open")}
                      className="nb-btn bg-white px-3 py-1.5 text-xs"
                    >
                      Reopen
                    </button>
                  )}
                  <button
                    data-testid={`bug-delete-${row.id}`}
                    onClick={() => remove(row)}
                    className="nb-btn nb-btn-danger px-3 py-1.5 text-xs ml-auto"
                    aria-label="delete"
                  >
                    <Trash2 size={12} strokeWidth={2.5} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {lightboxFor && (
        <Lightbox paths={lightboxFor} onClose={() => setLightboxFor(null)} />
      )}
    </div>
  );
};

export default BugReports;
